import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { supabase } from '../lib/supabase';

interface Borrower {
  id: string;
  name: string;
  mobile_no: string;
  address: string;
  collection_id: string; // Add this to store the collection ID
}

interface BorrowerUpdatesSearchAutocompleteProps {
  onBorrowerSelect: (collectionId: string | null) => void;
}

const BorrowerUpdatesSearchAutocomplete: React.FC<BorrowerUpdatesSearchAutocompleteProps> = ({ onBorrowerSelect }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Borrower[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedBorrower, setSelectedBorrower] = useState<Borrower | null>(null);
  const requestSequence = useRef(0);

  useEffect(() => {
    // Reset loading state when input is too short
    if (inputValue.length < 2) {
      requestSequence.current++;
      setLoading(false);
      setOptions([]);
      return;
    }

    const fetchBorrowers = async () => {
      const requestId = ++requestSequence.current;
      setLoading(true);
      try {
        // Query the borrower_updates table to get unique borrowers with their latest updates
        const { data, error } = await supabase
          .from('borrower_updates')
          .select(`
            collection:for_collection!collection_id (
              id,
              borrower:borrowers!inner (
                id,
                name,
                mobile_no,
                address
              )
            ),
            loandisk_assignment:loandisk_collection_assignments!loandisk_collection_assignment_id (
              id,
              loandisk_borrower_id,
              loandisk_unique_number
            )
          `)
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (requestId !== requestSequence.current) return;

        const assignmentBorrowerIds = Array.from(new Set((data || []).flatMap((update: any) => {
          const assignment = Array.isArray(update.loandisk_assignment)
            ? update.loandisk_assignment[0]
            : update.loandisk_assignment;
          return assignment?.loandisk_borrower_id ? [assignment.loandisk_borrower_id] : [];
        })));
        const mirrorById = new Map<string, any>();
        if (assignmentBorrowerIds.length > 0) {
          const { data: mirrorBorrowers, error: mirrorError } = await supabase
            .from('loandisk_borrowers')
            .select('loandisk_borrower_id, full_name, first_name, last_name, mobile, address')
            .in('loandisk_borrower_id', assignmentBorrowerIds);
          if (mirrorError) throw mirrorError;
          (mirrorBorrowers || []).forEach((borrower) => {
            mirrorById.set(borrower.loandisk_borrower_id, borrower);
          });
        }

        // Transform both legacy and LoanDisk-backed borrower updates.
        const borrowerMap = new Map<string, Borrower>();
        
        data?.forEach(update => {
          const collection = Array.isArray(update.collection) 
            ? update.collection[0] 
            : update.collection;
            
          if (collection?.borrower) {
            const borrower = Array.isArray(collection.borrower) 
              ? collection.borrower[0] 
              : collection.borrower;
              
            // Only add if the borrower name matches the search
            if (borrower.name.toLowerCase().includes(inputValue.toLowerCase())) {
              borrowerMap.set(borrower.id, {
                id: borrower.id,
                name: borrower.name,
                mobile_no: borrower.mobile_no,
                address: borrower.address,
                collection_id: collection.id
              });
            }
          }

          const assignment = Array.isArray((update as any).loandisk_assignment)
            ? (update as any).loandisk_assignment[0]
            : (update as any).loandisk_assignment;
          const mirrorBorrower = assignment
            ? mirrorById.get(assignment.loandisk_borrower_id)
            : null;
          if (assignment && mirrorBorrower) {
            const name = mirrorBorrower.full_name
              || [mirrorBorrower.first_name, mirrorBorrower.last_name].filter(Boolean).join(' ')
              || 'Unnamed borrower';
            if (name.toLowerCase().includes(inputValue.toLowerCase())) {
              borrowerMap.set(`loandisk:${assignment.id}`, {
                id: mirrorBorrower.loandisk_borrower_id,
                name,
                mobile_no: mirrorBorrower.mobile || '',
                address: mirrorBorrower.address || '',
                collection_id: assignment.id,
              });
            }
          }
        });

        // Convert map to array and sort by name
        const uniqueBorrowers = Array.from(borrowerMap.values())
          .sort((a, b) => a.name.localeCompare(b.name))
          .slice(0, 20); // Limit to 20 results

        if (requestId !== requestSequence.current) return;
        setOptions(uniqueBorrowers);
      } catch (error) {
        console.error('Error fetching borrowers with updates:', error);
        if (requestId === requestSequence.current) setOptions([]);
      } finally {
        if (requestId === requestSequence.current) setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchBorrowers, 300);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [inputValue]);

  return (
    <Autocomplete
      fullWidth
      options={options}
      getOptionLabel={(option) => option.name}
      filterOptions={(x) => x} // Disable client-side filtering
      loading={loading}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      onChange={(_, newValue) => {
        setSelectedBorrower(newValue);
        onBorrowerSelect(newValue ? newValue.collection_id : null);
        if (!newValue) setInputValue('');
      }}
      value={selectedBorrower}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={inputValue.length < 2 ? "Type at least 2 characters to search" : "No borrowers found"}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search borrower by name..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(optionProps, option) => {
        // Properly extract the key prop
        const { key, ...props } = optionProps;
        return (
          <li key={key} {...props}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.mobile_no} • {option.address}
              </Typography>
            </Box>
          </li>
        );
      }}
    />
  );
};

export default BorrowerUpdatesSearchAutocomplete;
